import { useSyncExternalStore } from 'react';

/**
 * Chế độ sáng/tối của giao diện — người dùng yêu cầu 2026-10-06.
 *
 * Ba lựa chọn: **theo hệ điều hành** (mặc định), **luôn sáng**, **luôn tối**.
 * Kết quả là lớp `.dark` trên `<html>` — `@custom-variant dark` ở `index.css`
 * và mọi token `.dark` đọc đúng lớp đó.
 *
 * ## Vì sao `localStorage`, không phải cấu hình server
 *
 * Cùng lý do `admin-home.ts`: đây là tiện lợi của **một người trên một máy**.
 * Máy làm việc để tối, điện thoại theo hệ điều hành, máy chiếu phòng họp để
 * sáng — đồng bộ qua server là đè lựa chọn của máy này bằng lựa chọn của máy
 * kia. Và lượt đọc phải xong **trước khi trang vẽ** (xem `index.html`), tức là
 * trước khi có phiên đăng nhập để hỏi server.
 *
 * ## ⚠️ Khoá và giá trị trùng với đoạn script trong `index.html`
 *
 * Script ấy bật `.dark` trước lần vẽ đầu — đợi bundle JS tải xong mới bật là
 * người chọn "Tối" thấy trang chớp trắng ở mỗi lần mở. Đổi `STORAGE_KEY` hay
 * tên giá trị thì đổi cả ở đó.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const STORAGE_KEY = 'apm.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Mọi lượt đọc bọc `try`: cửa sổ ẩn danh và chặn site data làm `localStorage` ném. */
function readPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.(DARK_QUERY).matches ?? false;
}

function apply(preference: ThemePreference): void {
  const dark = preference === 'dark' || (preference === 'system' && systemPrefersDark());
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  // Thanh cuộn, ô chọn ngày và các điều khiển gốc của trình duyệt đổi theo.
  root.style.colorScheme = dark ? 'dark' : 'light';
}

let current: ThemePreference = 'system';
const listeners = new Set<() => void>();

/**
 * Gọi **một lần** ở `main.tsx`. Áp lại lựa chọn đã lưu (script ở `index.html`
 * đã làm trước, đây là bản chuẩn) và nghe hệ điều hành đổi sáng/tối — máy tự
 * chuyển tối lúc hoàng hôn thì app ở chế độ "theo hệ điều hành" chuyển theo
 * mà không cần tải lại.
 */
export function initTheme(): void {
  current = readPreference();
  apply(current);

  window.matchMedia?.(DARK_QUERY).addEventListener('change', () => {
    if (current === 'system') apply('system');
  });

  // Đổi ở tab khác thì tab này đổi theo — hai tab một sáng một tối là lạ.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    current = isPreference(event.newValue) ? event.newValue : 'system';
    apply(current);
    listeners.forEach((listener) => listener());
  });
}

export function setThemePreference(preference: ThemePreference): void {
  current = preference;
  apply(preference);
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Không nhớ được thì thôi: lượt này vẫn đúng, lần sau về "theo hệ điều hành".
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, () => current);
}
